import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { SxStyle } from '../../../../../types';
import { useStringsContext } from '../../../../../contexts/strings';
import { SquadPlayer } from '../../types';
import { CurrentClub } from '../../api-types';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import TableBody from '@mui/material/TableBody';
import { translateCountry } from '../../../../../countries';
import PlayerTabContent from './player-tab-content';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';

interface FilteredInfo
  extends Pick<
      SquadPlayer,
      | 'fullName'
      | 'displayName'
      | 'position'
      | 'agent'
      | 'birthDay'
      | 'height'
      | 'weight'
      | 'foot'
      | 'nationality'
      | 'youthClub'
    >,
    Pick<
      CurrentClub,
      // 'active'|
      'shirtNumber' | 'status'
    > {
  // Additional fake properties
  age: string;
}

const filteredInfoKeys: (keyof FilteredInfo)[] = [
  'fullName',
  'displayName',
  'shirtNumber',
  'position',
  'agent',
  // 'active',
  'status',
  'birthDay',
  'age',
  'height',
  'weight',
  'foot',
  'nationality',
  'youthClub',
];

interface InfoRowsType {
  key: keyof FilteredInfo;
  value: FilteredInfo[keyof FilteredInfo] | JSX.Element;
}

const styles: SxStyle = {
  tableRow: (theme) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }),
  tableCellLeft: (theme) => ({
    [theme.breakpoints.up('md')]: {
      width: '40%', // Arbitrary value to limit the width on wide screens
    },
  }),
};

interface Props {
  teamId: string;
  player: SquadPlayer;
  onUpdatePlayer: (player: SquadPlayer) => void;
  onEditPlayer: () => void;
  season: number;
  isReadOnly?: boolean;
}

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;
const CURRENT_YEAR = parseInt(REACT_APP_DEFAULT_LEAGUE_SEASON as string, 10);

const PlayerTabInfo = ({
  teamId,
  player,
  onUpdatePlayer,
  onEditPlayer,
  season,
  isReadOnly,
}: Props) => {
  const { strings } = useStringsContext();
  const [rows, setRows] = useState<InfoRowsType[] | null>(null);

  useEffect(() => {
    if (player) {
      const infoRows = filteredInfoKeys.map((key) => {
        let value = null;
        if (key === 'position') {
          const position = player.position;
          let positions = position?.primary ? [position.primary] : [];
          positions = position?.other ? [...positions, ...position.other] : positions;
          value = positions.map((p) => strings.squadPlayerPositions[p]).join(' / ');
        } else if (key === 'age') {
          value = moment().diff(player.birthDay, 'years', false);
        } else if (key === 'weight') {
          value = player.weight ? player.weight + ' kg' : '';
        } else if (key === 'height') {
          value = player.height ? player.height + ' cm' : '';
          /*
        Disabled (https://linear.app/sef/issue/SMA-493)
        } else if (key === 'active') {
          value = <PlayerActiveSelect teamId={teamId} player={player} onUpdatePlayer={onUpdatePlayer} />
        */
        } else if (key === 'shirtNumber') {
          value = player.activeCurrentClub?.shirtNumber;
        } else if (key === 'foot') {
          value = player.foot ? strings.squadPlayer[player.foot] : '';
        } else if (key === 'nationality') {
          value = player.nationality ? translateCountry(player.nationality) : '';
        } else if (key === 'status') {
          value = player.activeCurrentClub?.status
            ? strings.squadPlayer[player.activeCurrentClub.status]
            : '';
        } else {
          value = player[key];
        }
        return { key: key, value: value };
      });
      setRows(infoRows);
    }
  }, [teamId, player, strings, onUpdatePlayer]);

  if (!rows) {
    return <></>;
  }

  return (
    <PlayerTabContent
      title={strings.squadPlayerTabs.information}
      buttonElement={
        <Button
          variant="contained"
          onClick={onEditPlayer}
          startIcon={<EditIcon />}
          disabled={season !== CURRENT_YEAR || isReadOnly}
        >
          {strings.squad.edit}
        </Button>
      }
    >
      <TableBody>
        {rows?.map((row) => (
          <TableRow key={row.key} sx={styles.tableRow}>
            <TableCell sx={styles.tableCellLeft}>
              <Typography variant="subtitle2">{strings.squadPlayer[row.key]}</Typography>
            </TableCell>
            <TableCell>
              {row.value?.toString() || (
                <Typography variant="italic">{strings.squad.missingInformation}</Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </PlayerTabContent>
  );
};

export default PlayerTabInfo;
